import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { PermissionButton } from 'src/routes/components/permission-button';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  stErrorMessage,
  stClaimThreshold,
  stEligibilityThreshold,
  stPlatformBalances,
  stUpdateClaimThreshold,
  stUpdateEligibilityThreshold,
  stUpdatePlatformBudget,
  StClaimThreshold,
  StEligibilityThreshold,
  StPlatformBalance,
} from '../../../services/socialTradingService';
import { StBooleanChip, StScrollTable, stText } from '../StListUi';
import DisplayRulesSection from './DisplayRulesSection';

/** Claimed = Allocated(spent) − Frozen; clamp at 0 for display. */
function claimedBalance(spent: string, frozen: string): string {
  const allocated = Number(spent);
  const frozenAmt = Number(frozen);
  if (!Number.isFinite(allocated) || !Number.isFinite(frozenAmt)) return '0';
  const claimed = allocated - frozenAmt;
  return String(claimed < 0 ? 0 : claimed);
}

export default function SocialTradingRedpacketConfigList() {
  const t = useTranslate();
  const notify = useNotify();
  const [balances, setBalances] = useState<StPlatformBalance[]>([]);
  const [threshold, setThreshold] = useState<StClaimThreshold | null>(null);
  const [eligibility, setEligibility] = useState<StEligibilityThreshold | null>(null);
  const [budget, setBudget] = useState('');
  const [budgetPool, setBudgetPool] = useState('OFFICIAL');
  const [thresholdEnabled, setThresholdEnabled] = useState(true);
  const [minClaimAmount, setMinClaimAmount] = useState('1');
  const [eligibilityEnabled, setEligibilityEnabled] = useState(true);
  const [minTradingVolume, setMinTradingVolume] = useState('100');

  const load = useCallback(async () => {
    try {
      const [b, cfg, elig] = await Promise.all([
        stPlatformBalances(),
        stClaimThreshold(),
        stEligibilityThreshold(),
      ]);
      setBalances(b || []);
      setThreshold(cfg || null);
      if (cfg) {
        setThresholdEnabled(!!cfg.enabled);
        setMinClaimAmount(cfg.minClaimAmount || '1');
      }
      setEligibility(elig || null);
      if (elig) {
        setEligibilityEnabled(!!elig.enabled);
        setMinTradingVolume(elig.minTradingVolume || '100');
      }
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const saveBudget = async () => {
    try {
      await stUpdatePlatformBudget({ poolType: budgetPool, totalBudget: budget });
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const saveThreshold = async () => {
    try {
      await stUpdateClaimThreshold({
        enabled: thresholdEnabled,
        minClaimAmount,
      });
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  const saveEligibility = async () => {
    try {
      await stUpdateEligibilityThreshold({
        enabled: eligibilityEnabled,
        minTradingVolume,
      });
      notify(t('module.socialTrading.saveOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(stErrorMessage(e), { type: 'error' });
    }
  };

  return (
    <PermissionGuard resource="st-redpacket-config" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.redpacketConfig')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.redpacketConfig') },
          ]}
          action={
            <Button size="small" variant="outlined" onClick={load}>
              {t('module.socialTrading.refresh')}
            </Button>
          }
        />
        <Stack spacing={2} sx={{ mt: 2 }}>
          <DisplayRulesSection />

          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('module.socialTrading.platformBudget')}
              </Typography>
              <StScrollTable>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('module.socialTrading.poolType')}</TableCell>
                    <TableCell>{t('module.socialTrading.totalBudget')}</TableCell>
                    <TableCell>{t('module.socialTrading.available')}</TableCell>
                    <TableCell>{t('module.socialTrading.allocated')}</TableCell>
                    <TableCell>{t('module.socialTrading.frozen')}</TableCell>
                    <TableCell>{t('module.socialTrading.claimed')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {balances.map((b) => (
                    <TableRow key={b.poolType}>
                      <TableCell>{stText(b.poolType)}</TableCell>
                      <TableCell>{stText(b.totalBudget)}</TableCell>
                      <TableCell>{stText(b.availableBalance)}</TableCell>
                      <TableCell>{stText(b.spentBalance)}</TableCell>
                      <TableCell>{stText(b.frozenBalance)}</TableCell>
                      <TableCell>{stText(claimedBalance(b.spentBalance, b.frozenBalance))}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </StScrollTable>
              <Stack direction="row" spacing={1}>
                <TextField size="small" label="poolType" value={budgetPool} onChange={(e) => setBudgetPool(e.target.value)} />
                <TextField size="small" label="totalBudget" value={budget} onChange={(e) => setBudget(e.target.value)} />
                <PermissionButton resource="st-redpacket-config" action="update">
                  <Button variant="contained" onClick={saveBudget}>
                    {t('module.socialTrading.save')}
                  </Button>
                </PermissionButton>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('module.socialTrading.thresholdList')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t('module.socialTrading.thresholdListHint')}
              </Typography>
              <List disablePadding>
                <ListItem
                  alignItems="flex-start"
                  sx={{
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { md: 'center' },
                    gap: 1.5,
                    px: 0,
                    py: 1.5,
                  }}
                >
                  <ListItemText
                    sx={{ flex: '1 1 220px', m: 0 }}
                    primary={t('module.socialTrading.claimThreshold')}
                    secondary={
                      threshold
                        ? t('module.socialTrading.claimThresholdCurrent', {
                            enabled: String(threshold.enabled),
                            min: stText(threshold.minClaimAmount),
                          })
                        : t('module.socialTrading.claimThresholdHint')
                    }
                    primaryTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                  />
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {threshold && <StBooleanChip value={threshold.enabled} />}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={thresholdEnabled}
                          onChange={(e) => setThresholdEnabled(e.target.checked)}
                        />
                      }
                      label="enabled"
                    />
                    <TextField
                      size="small"
                      label="minClaimAmount"
                      value={minClaimAmount}
                      onChange={(e) => setMinClaimAmount(e.target.value)}
                      sx={{ width: 160 }}
                    />
                    <PermissionButton resource="st-redpacket-config" action="update">
                      <Button variant="contained" onClick={saveThreshold}>
                        {t('module.socialTrading.save')}
                      </Button>
                    </PermissionButton>
                  </Stack>
                </ListItem>

                <Divider component="li" />

                <ListItem
                  alignItems="flex-start"
                  sx={{
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { md: 'center' },
                    gap: 1.5,
                    px: 0,
                    py: 1.5,
                  }}
                >
                  <ListItemText
                    sx={{ flex: '1 1 220px', m: 0 }}
                    primary={t('module.socialTrading.eligibilityThreshold')}
                    secondary={
                      eligibility
                        ? t('module.socialTrading.eligibilityThresholdCurrent', {
                            enabled: String(eligibility.enabled),
                            min: stText(eligibility.minTradingVolume),
                          })
                        : t('module.socialTrading.eligibilityThresholdHint')
                    }
                    primaryTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                  />
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {eligibility && <StBooleanChip value={eligibility.enabled} />}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={eligibilityEnabled}
                          onChange={(e) => setEligibilityEnabled(e.target.checked)}
                        />
                      }
                      label="enabled"
                    />
                    <TextField
                      size="small"
                      label="minTradingVolume"
                      value={minTradingVolume}
                      onChange={(e) => setMinTradingVolume(e.target.value)}
                      sx={{ width: 160 }}
                    />
                    <PermissionButton resource="st-redpacket-config" action="update">
                      <Button variant="contained" onClick={saveEligibility}>
                        {t('module.socialTrading.save')}
                      </Button>
                    </PermissionButton>
                  </Stack>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Stack>
      </DashboardContent>
    </PermissionGuard>
  );
}
